// src/pages/Login.js
import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [formError, setFormError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  
  const { login, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      await login(formData);
      navigate('/predictions');
    } catch (err) {
      setFormError(err.response?.data?.message || 'Login failed');
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    
    if (!resetEmail) {
      setResetMessage('Please enter your email address');
      return;
    }
    
    try {
      setResetLoading(true);
      
      // Call your password reset API endpoint
      const response = await fetch(`/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to request password reset');
      }
      
      setResetMessage('Password reset email sent. Please check your inbox.');
      setResetEmail('');
      
      // Optionally close the reset form after a successful submission
      setTimeout(() => {
        setShowResetForm(false);
      }, 3000);
      
    } catch (err) {
      setResetMessage(err.message || 'Failed to request password reset');
    } finally {
      setResetLoading(false);
    }
  };
  
  return (
    <div className="max-w-md mx-auto mt-10 bg-gray-700 p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center text-white">Login</h2>
      
      {formError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {formError}
        </div>
      )}
      
      {!showResetForm ? (
        <>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-white mb-2">Username:</label>
              <input 
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full p-2 border rounded text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white mb-2">Password:</label>
              <input 
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 border rounded text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <div className="mt-4 text-center">
            <p>
              <button 
                onClick={() => setShowResetForm(true)}
                className="text-white hover:underline"
              >
                Forgot Password?
              </button>
            </p>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-white">
              Don't have an account?{' '}
              <Link to="/register" className="!text-red-500 hover:underline visited:!text-red-500" style={{color: '#ef4444 !important'}}>
                Register
              </Link>
            </p>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-lg font-medium mb-4 text-white">Reset Password</h3>
          
          {resetMessage && (
            <div className={`px-4 py-3 rounded mb-4 ${resetMessage.includes('sent') ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'}`}>
              {resetMessage}
            </div>
          )}
          
          <form onSubmit={handleResetPassword}>
            <div className="mb-4">
              <label className="block text-white mb-2">Email Address:</label>
              <input 
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full p-2 border rounded text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email address"
                required
              />
            </div>
            
            <div className="flex space-x-2">
              <button 
                type="submit" 
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  setShowResetForm(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300"
                disabled={resetLoading}
              >
                Back to Login
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default Login;